"use client";

import { useState, useEffect, use } from "react";
import { createClient } from "@/lib/supabase/client";
import { PRIMARY_BTN } from "@/components/ui/button-styles";

export default function LoginPage({
  searchParams,
}: {
  searchParams: Promise<{ error?: string; next?: string }>;
}) {
  const { error: searchError, next } = use(searchParams);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Handle invite tokens delivered via hash fragment (implicit flow).
  // The @supabase/ssr browser client does not auto-detect hash fragments,
  // so we manually extract tokens and call setSession.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.includes("access_token")) return;

    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const isInvite = params.get("type") === "invite";

    if (!accessToken || !refreshToken) return;

    const supabase = createClient();
    supabase.auth
      .setSession({ access_token: accessToken, refresh_token: refreshToken })
      .then(({ error }) => {
        if (error) {
          setAuthError(error.message);
          return;
        }
        window.location.href = isInvite ? "/auth/set-password" : "/";
      });
  }, []);

  // Suppress error message when hash contains valid auth tokens (invite flow)
  const hasHashToken = typeof window !== "undefined" && window.location.hash.includes("access_token");
  const errorMsg =
    hasHashToken
      ? null
      : searchError === "domain"
        ? "Only @sparkbusinessworks.com accounts may sign in with Google."
        : searchError === "auth"
          ? "Authentication failed. Please try again."
          : null;
  const safeNext = next?.startsWith("/") && !next.startsWith("//") ? next : "/";

  async function handleGoogleSignIn() {
    setLoading(true);
    const supabase = createClient();
    const callbackUrl = new URL("/auth/callback", location.origin);
    if (safeNext !== "/") callbackUrl.searchParams.set("next", safeNext);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: callbackUrl.toString(),
        queryParams: { hd: "sparkbusinessworks.com" },
      },
    });
  }

  async function handleEmailSignIn(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setAuthError(null);
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      setAuthError(error.message);
      setLoading(false);
    } else {
      window.location.href = safeNext;
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-spark-lightest-gray px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <span className="text-2xl font-bold tracking-tight text-spark-black">
            SPARK
          </span>
          <span className="ml-1 text-lg font-medium text-spark-charcoal">
            Analytics
          </span>
        </div>

        {errorMsg && (
          <div role="alert" className="rounded-md border border-spark-red-tint bg-spark-red-tint/40 px-4 py-3 text-sm text-spark-red-text">
            {errorMsg}
          </div>
        )}

        {/* Spark team */}
        <div className="rounded-lg border border-spark-light-gray bg-white p-6 shadow-[var(--shadow-card)]">
          <h2 className="mb-4 text-sm font-semibold text-spark-charcoal">
            Spark team
          </h2>
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="flex w-full items-center justify-center gap-3 rounded-md border border-spark-light-gray bg-white px-4 py-2 text-sm font-medium text-spark-charcoal shadow-[var(--shadow-input)] hover:bg-spark-lightest-gray disabled:opacity-50 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>
        </div>

        {/* Client portal */}
        <div className="rounded-lg border border-spark-light-gray bg-white p-6 shadow-[var(--shadow-card)]">
          <h2 className="mb-4 text-sm font-semibold text-spark-charcoal">
            Client portal
          </h2>
          <form onSubmit={handleEmailSignIn} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="mb-1 block text-sm font-medium text-spark-charcoal"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-md border border-spark-light-gray px-3 py-2 text-sm shadow-[var(--shadow-input)] focus:border-spark-blue focus:ring-[3px] focus:ring-spark-blue/25 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-sm font-medium text-spark-charcoal"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-md border border-spark-light-gray px-3 py-2 text-sm shadow-[var(--shadow-input)] focus:border-spark-blue focus:ring-[3px] focus:ring-spark-blue/25 focus:outline-none transition-colors"
              />
            </div>
            {authError && (
              <p role="alert" className="text-sm text-spark-red-text">{authError}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className={`w-full ${PRIMARY_BTN}`}
            >
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
